// src/encounter/lib/medication-frequency.ts
// Medication frequency model (PROD-1340) — ports the Flutter dose form's frequency logic
// (new_medication_add_update_widget.dart). The chosen frequency type drives which conditional
// fields show and how the wire `occurenceRequest[]` is built. Pure — no I/O.
import type { OccurenceRequest } from '@/types/assessment-api';

/**
 * The 5 frequency codes (wire `frequency` value). These MUST match Flutter's `AppConstants`
 * (`app_constants.dart`) AND the BE's `FrequencyEnum` (Scrips.Patient PatientMedication.cs) VERBATIM:
 * `everyday / needed / day_interval / specific / now`. The assessment save stores the string opaquely,
 * but the order-send → e-prescription path parses it into `FrequencyEnum` — a non-matching value
 * (the earlier `every_day/as_needed/days_interval/specific_days`) mis-parses there (PROD-1445).
 */
export const MED_FREQUENCY = {
  everyDay: 'everyday',
  asNeeded: 'needed',
  now: 'now',
  daysInterval: 'day_interval',
  specificDays: 'specific',
} as const;
export type MedFrequency = (typeof MED_FREQUENCY)[keyof typeof MED_FREQUENCY];

/**
 * The FREQUENCY pill row, in the live dialog's order and copy (captured 2026-07-30,
 * dev-provider.scrips.com). The sequence and two of the labels were wrong here: the pills say
 * "Days interval", not "Every N days".
 *
 * `Now` IS OFFERED NO LONGER (Andrew, 30 Jul: "now is hidden in dev") — which is also why the Figma
 * frame shows four pills, not five. The `now` VALUE and every code path that depends on it are kept
 * deliberately: a medication saved earlier can still carry `now`, and it must reload and render
 * correctly (weight + priority, no schedule) rather than falling back to a wrong frequency. Hiding
 * the option is not the same as deleting the concept — see MED_FREQUENCY_ALL below.
 */
export const MED_FREQUENCY_OPTIONS: ReadonlyArray<{ value: MedFrequency; label: string }> = [
  { value: MED_FREQUENCY.asNeeded, label: 'As needed' },
  { value: MED_FREQUENCY.everyDay, label: 'Every day' },
  { value: MED_FREQUENCY.daysInterval, label: 'Days interval' },
  { value: MED_FREQUENCY.specificDays, label: 'Specific days' },
];

/**
 * Every frequency INCLUDING the hidden `now` — for reading a saved order back. Re-add `now` to
 * MED_FREQUENCY_OPTIONS to unhide it; nothing else needs to change.
 */
export const MED_FREQUENCY_ALL: ReadonlyArray<{ value: MedFrequency; label: string }> = [
  { value: MED_FREQUENCY.now, label: 'Now' },
  ...MED_FREQUENCY_OPTIONS,
];

/** Two-letter weekday chips as the live dialog renders them: Mo Tu Wd Th Fr Sa Su. */
export const WEEKDAY_SHORT: Readonly<Record<string, string>> = {
  Monday: 'Mo',
  Tuesday: 'Tu',
  Wednesday: 'Wd',
  Thursday: 'Th',
  Friday: 'Fr',
  Saturday: 'Sa',
  Sunday: 'Su',
};

export const WEEKDAYS = [
  'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday',
] as const;
export type Weekday = (typeof WEEKDAYS)[number];

/**
 * Wire weekday CODES for specific_days `occurenceRequest.periodUnit` — MUST match Flutter's
 * `AppConstants.weekDays` VERBATIM: `mo/tu/wd/th/fr/sa/su` (note `wd` Wednesday, `th` Thursday).
 * The UI shows the full `Weekday` name; the wire carries the code (PROD-1445). Sending the full
 * name ("Monday") diverges from Flutter/BE and breaks the specific_days round-trip.
 */
export const WEEKDAY_CODE: Record<Weekday, string> = {
  Monday: 'mo', Tuesday: 'tu', Wednesday: 'wd', Thursday: 'th', Friday: 'fr', Saturday: 'sa', Sunday: 'su',
};
const CODE_WEEKDAY: Record<string, Weekday> = Object.fromEntries(
  Object.entries(WEEKDAY_CODE).map(([name, code]) => [code, name as Weekday]),
) as Record<string, Weekday>;

export const MED_PRIORITIES = ['routine', 'urgent', 'asap', 'stat'] as const;
export type MedPriority = (typeof MED_PRIORITIES)[number];

export const DURATION_UNITS = ['day', 'wk', 'mo'] as const;
export type DurationUnit = (typeof DURATION_UNITS)[number];

/** The occurrence inputs the dialog captures, independent of the wire shape. */
export interface OccurrenceSpec {
  frequency: MedFrequency;
  /** Units per intake → occurenceRequest.count. */
  quantity?: number;
  /** Times a day → occurenceRequest.period. */
  timesADay?: number;
  /** Every N days (days_interval only) → occurenceRequest.daysInterval. */
  daysInterval?: number;
  /** Weekdays (specific_days only) → one occurenceRequest each, periodUnit = day. */
  specificDays?: string[];
}

/** Whether this frequency type schedules an intake (everything except as-needed / now). */
export function frequencyIsScheduled(frequency: MedFrequency): boolean {
  return frequency !== MED_FREQUENCY.asNeeded && frequency !== MED_FREQUENCY.now;
}

function baseOccurrence(zeroGuid: string): OccurenceRequest {
  return {
    count: null,
    countMax: null,
    daysInterval: null,
    period: null,
    periodMax: null,
    periodUnit: null,
    occurenceTiming: null,
    isActive: true,
    isDeleted: false,
    medicationId: zeroGuid,
  };
}

/**
 * Build the wire occurenceRequest[] for a frequency (Flutter new_medication_add_update_widget.dart:1291-1315):
 *   • as_needed / now → [] (no schedule; "now" carries weight/priority on the medication, not here)
 *   • every_day       → 1 entry  {count: quantity, period: timesADay}
 *   • days_interval   → 1 entry  {count, period, daysInterval}
 *   • specific_days   → 1 entry per weekday {count, period, periodUnit: <weekday CODE mo/tu/…>}
 */
export function buildOccurrenceRequests(spec: OccurrenceSpec, zeroGuid: string): OccurenceRequest[] {
  const count = spec.quantity != null ? String(spec.quantity) : null;
  const period = spec.timesADay ?? null;

  switch (spec.frequency) {
    case MED_FREQUENCY.everyDay:
      return [{ ...baseOccurrence(zeroGuid), count, period }];
    case MED_FREQUENCY.daysInterval:
      return [{ ...baseOccurrence(zeroGuid), count, period, daysInterval: spec.daysInterval ?? null }];
    case MED_FREQUENCY.specificDays:
      return (spec.specificDays ?? []).map((day) => ({
        ...baseOccurrence(zeroGuid),
        count,
        period,
        // Wire carries the Flutter weekday CODE (mo/tu/…); fall back to the raw value if it's already
        // a code (e.g. a Flutter-saved med reloaded and re-saved).
        periodUnit: WEEKDAY_CODE[day as Weekday] ?? day,
      }));
    case MED_FREQUENCY.asNeeded:
    case MED_FREQUENCY.now:
    default:
      return [];
  }
}

/** Inverse of buildOccurrenceRequests — recover the dialog inputs from a loaded occurenceRequest[]. */
export function parseOccurrenceRequests(
  reqs: OccurenceRequest[] | null | undefined,
  frequency: string | null | undefined,
): { quantity?: number; timesADay?: number; daysInterval?: number; specificDays?: string[] } {
  if (!reqs || reqs.length === 0) return {};
  const first = reqs[0];
  const out: { quantity?: number; timesADay?: number; daysInterval?: number; specificDays?: string[] } = {};
  if (first.count != null && first.count !== '') {
    const n = Number(first.count);
    if (!Number.isNaN(n)) out.quantity = n;
  }
  if (first.period != null) out.timesADay = first.period;
  if (first.daysInterval != null) out.daysInterval = first.daysInterval;
  if (frequency === MED_FREQUENCY.specificDays) {
    // periodUnit is the wire weekday CODE (mo/tu/…) → map back to the full Weekday name the UI uses.
    // Tolerate a legacy full-name value (older React saves) by passing it through unmapped.
    const days = reqs
      .map((r) => r.periodUnit)
      .filter((d): d is string => !!d)
      .map((code) => CODE_WEEKDAY[code] ?? code);
    if (days.length > 0) out.specificDays = days;
  }
  return out;
}
